import React, { useState, useCallback, useEffect, Fragment } from 'react';
import Router from 'next/router';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Typo, Input, Dropdown, Form, Buttons } from 'tmt-ui-kit';
import isEmpty from 'lodash/isEmpty';
// import { scroller } from 'react-scroll';

import { actions as sessionActions } from '~/redux/reducers/session';

import useSignUpForm, { schema } from './hooks/useSignUpForm';
import Submitting from './Submitting';
import config from '~/config';
import { path } from '~/utils/media';
import scrollToFn from '~/utils/scroller';
import Link from '~/components/Link/Link';


const MonthlyObj = [
  'Less than 5,000',
  '5,000 to 10,000',
  '10,000 to 15,000',
  '15,000 to 20,000',
  'More than 20,000',
];

const Container = styled.div`
  box-sizing: border-box;
  box-shadow: 0 9px 12px 0 rgba(0, 0, 0, 0.1);
  transform: translateY(-80px);
  background: white;
  border-radius: 4px;

  @media(max-width: 768px) {
    transform: translateY(0px);
    margin-top: 16px;
  }
`;

const Header = styled.div`
  padding: 34px 49px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;

  @media(max-width: 600px) {
    padding: 12px 16px 24px;;
  }
`;

const Title = styled(Typo.HeaderTwo)`
  color: ${props => props.theme.greenLighter} !important;
  font-size: 24px !important;
  font-weight: 600 !important;
`;

const Text = styled(Typo.Paragraph)`
  font-size: 16px !important;
`;

const FormContainer = styled.form`
  background: ${props => props.theme.blueGrayLighter};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  padding: 24px 0px 0px;
`;

const TitleForm = styled(Typo.HeaderTwo)`
  border-left: 5px solid ${props => props.theme.orange};
  padding: 8px 24px;
`;

const Padding = styled.div`
  padding: 24px 50px;
  p { 
    text-align: right;
  }

  @media(max-width: 600px) {
    padding: 16px 24px;
  }
`;

const Label = styled(Typo.HeaderFour)`
  display: flex;
  ${props => props.margin && `margin: ${props.margin};`}
`;

const MonthlyDropdown = styled(Dropdown.Dropdown)`
  border: 1px solid ${props => (props.error ? props.theme.red : props.theme.lightGray1)};
  background: ${props => props.theme.white};
  margin: 8px 0px;

  >p {
    color: ${props => props.isChangeColor && `${props.theme.lightGray} !important;`};
    text-align: left;
  }
`;

const CheckBox = styled(Form.CheckBox)`
  margin: 36px 0;
  /* align-items: center; */
`;

const LabelCheckbox = styled(Text)`
  margin: 0px;
  text-align: left !important;
`;

const TextBlue = styled.span`
  color: ${props => props.theme.blue} !important;
`;

const Submit = styled(Buttons.Button.OrangeButton)`
  width: 148px;
  margin: 32px auto 0px;
  display: block;

  ${props => props.disabled && `
    filter: grayscale(100%);
    cursor: not-allowed;
    * {
      cursor: not-allowed;
      pointer-events:none;
    }
  `}
`;

const Important = styled.div`
  color: ${props => props.theme.red};
  margin-left: 4px;
`;

const TextError = styled(Typo.HeaderTwo)`
  color: ${props => props.theme.red} !important;
  text-align: center;
  margin-top: 40px;
`;

const ProfileContainer = styled.div`
  border-radius: 4px;
  padding: 14px 24px;
  background: ${props => props.theme.white};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;

  @media(max-width: 600px) {
    display: block;
  }
`;

const Avatar = styled.img`
  display: block;
  max-width: 50px;
  border-radius: 100px;
`;

const Profile = styled.div`
  display: flex;
  align-items: center;
  @media(max-width: 600px) {
    align-items: flex-start;
  }
`;

const TextName = styled(Typo.Paragraph)`
  ${props => props.bold && 'font-weight: 500 !important;'}
  text-align: left !important;
  margin: 0px 16px;
  word-break: break-all;
`;

const LogOut = styled(Typo.Paragraph)`
  font-weight: 500 !important;
  color: ${props => props.theme.blue} !important;
  width: 60px;
  flex: 1;
  margin: 0;
  cursor: pointer;

  @media(max-width: 600px) {
    margin: 8px 16px 0px;
  }
`;

const Desktop = styled.div`
  @media(max-width: 600px) {
    display: none;
  }
`;

const Mobile = styled.div`
  display: none;
  @media(max-width: 600px) {
    display: block;
    width: fit-content;
  }
`;

const MarginBottom = styled.div`
  margin-bottom: 18px;
`;

const enhance = connect(
  state => ({
    user: state.session.user,
    signingUp: state.session.signingUp,
    logout: state.session.logout,
    signupErrorMessage: state.session.signupErrorMessage,
    isLoggedIn: state.session.isLoggedIn,
  }),
  {
    ...sessionActions,
  }
);

function scrollTo(name, isMobile) {
  scrollToFn(name, {
    offset: isMobile ? -100 : -200,
    duration: 500,
    smooth: true,
  });
}

const Register = (props) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(async (data) => {
    const isValid = await schema.isValid(data);
    if (isValid) {
      const affiliateDetail = {
        is_assigned_reference_id: false,
        website_name: data.websiteName,
        website_url: data.websiteUrl,
        target_audience_country: data.targetCountry,
        monthly_visitors: data.monthly,
        is_gdpr_consent: data.isGdpr,
      };

      if (data.feacbookUrl) affiliateDetail.facebook_url = data.feacbookUrl;
      if (data.instagramUrl) affiliateDetail.instagram_url = data.instagramUrl;
      if (data.twitterUrl) affiliateDetail.twitter_url = data.twitterUrl;
      if (data.youtubeUrl) affiliateDetail.youtube_url = data.youtubeUrl;

      if (isEmpty(props.user)) {
        const signUpData = {
          email: data.email,
          password: data.password,
          name: {
            first: data.firstName,
            last: data.lastName,
          },
        };
        await props.signup(signUpData);
        await props.setAffiliateDetail(affiliateDetail);
      } else {
        await props.setAffiliateDetail(affiliateDetail);
      }

      setIsSubmitting(true);
      const scrollName = props.isMobile ? 'register-mebile' : 'register-desktop';
      return scrollTo(scrollName, props.isMobile);
    }
    return false;
  }, [props]);

  const signUpForm = useSignUpForm(undefined, onSubmit);
  const { user } = props;

  useEffect(() => {
    if (!isEmpty(user)) signUpForm.change('isUser', true);
  }, [user]);

  useEffect(() => {
    if (!isEmpty(signUpForm.errors)) {
      const scrollName = props.isMobile ? 'register-mebile' : 'register-desktop';
      scrollTo(scrollName, true);
    }
  }, [signUpForm.errors]);

  let isDisabled = !signUpForm.values.isGdpr || !signUpForm.values.isAgree;
  if (isEmpty(user)) {
    isDisabled = !signUpForm.values.isGdpr || !signUpForm.values.isAgree || props.signingUp;
  }
  const logout = () => props.logout().then(() => Router.push('/affiliate'));
  return (
    <Container>
      <Header>
        {!isSubmitting ? (
          <Fragment>
            <Title>Join us as an affiliate today!</Title>
            <Text>We&apos;re delighted to have you! <b/>Please register in this form. We&apos;ll get back to you asap!</Text>
            <Text>For any further queries, please contact our team at <b/><TextBlue>affiliate@takemetour.com</TextBlue></Text>
          </Fragment>
        ) : (
          <Fragment>
            <Submitting />
          </Fragment>
        )}
      </Header>
      {!isSubmitting && (
        <FormContainer {...signUpForm.getFormProps()}>
          {!isEmpty(user) ? (
            <Padding>
              <ProfileContainer>
                <Profile>
                  <Avatar
                    src={user.avatar_image ? path(user.avatar_image, 'lxAvatar') : `${config.assetPath}/static/images/default-img.jpg`}
                    srcSet={user.avatar_image ? `${path(user.avatar_image, 'lxAvatar2x')} 2x` : `${config.assetPath}/static/images/default-img.jpg`}
                    alt={user.name && `${user.name.first} ${user.name.last}`}
                  />
                  <div>
                    <TextName bold>{user.name && `${user.name.first} ${user.name.last}`}</TextName>
                    <TextName>{user && user.email}</TextName>
                    <Mobile>
                      <LogOut onClick={logout}>Log Out</LogOut>
                    </Mobile>
                  </div>
                </Profile>
                <Desktop>
                  <LogOut onClick={logout}>Log Out</LogOut>
                </Desktop>
              </ProfileContainer>
            </Padding>
          ) : (
            <Fragment>
              <TitleForm>Personal Info</TitleForm>
              <Padding>
                <Label>Username</Label>
                <MarginBottom>
                  <Input.NameInput
                    firstNameRef="firstName"
                    lastNameRef="lastName"
                    firstPlaceholder="First name"
                    lastPlaceholder="Last name"
                    handleOnChange={e => {
                      return e.target.name === 'firstName'
                        ? signUpForm.change('firstName', e.target.value)
                        : signUpForm.change('lastName', e.target.value);
                    }}
                    firstNameValue={signUpForm.values.firstName}
                    lastNameValue={signUpForm.values.lastName}
                    firstNameErrorMessage={signUpForm.errors.firstName}
                    lastNameErrorMessage={signUpForm.errors.lastName}
                  />
                </MarginBottom>
                <Label>Email</Label>
                <MarginBottom>
                  <Input.Input
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={signUpForm.values.email}
                    handleOnChange={(e) => signUpForm.change('email', e.target.value)}
                    errorMessage={signUpForm.errors.email}
                  />
                </MarginBottom>
                <Label>Password</Label>
                <MarginBottom>
                  <Input.Input
                    type="password"
                    name="password"
                    placeholder="Password"
                    showPassword
                    value={signUpForm.values.password}
                    handleOnChange={(e) => signUpForm.change('password', e.target.value)}
                    errorMessage={signUpForm.errors.password}
                  />
                </MarginBottom>
              </Padding>
            </Fragment>
          )}
          <TitleForm>Website Info</TitleForm>
          <Padding>
            <Label>Website Name <Important>*</Important></Label>
            <MarginBottom>
              <Input.Input
                type="text"
                name="websiteName"
                placeholder="Your answer"
                value={signUpForm.values.websiteName}
                handleOnChange={(e) => signUpForm.change('websiteName', e.target.value)}
                errorMessage={signUpForm.errors.websiteName}
              />
            </MarginBottom>
            <Label>Website URL <Important>*</Important></Label>
            <MarginBottom>
              <Input.Input
                type="text"
                name="websiteUrl"
                placeholder="Your answer"
                value={signUpForm.values.websiteUrl}
                handleOnChange={(e) => signUpForm.change('websiteUrl', e.target.value)}
                errorMessage={signUpForm.errors.websiteUrl}
              />
            </MarginBottom>
            <Label>Target Audience Country <Important>*</Important></Label>
            <MarginBottom>
              <Input.Input
                type="text"
                name="targetCountry"
                placeholder="Your answer"
                value={signUpForm.values.targetCountry}
                handleOnChange={(e) => signUpForm.change('targetCountry', e.target.value)}
                errorMessage={signUpForm.errors.targetCountry}
              />
            </MarginBottom>
            <Label>Monthly Visitors <Important>*</Important></Label>
            <MarginBottom>
              <MonthlyDropdown
                options={MonthlyObj.map(c => ({ label: c, value: c }))}
                onChange={(value) => signUpForm.change('monthly', value)}
                selected={signUpForm.values.monthly}
                isChangeColor={!signUpForm.values.monthly}
                error={signUpForm.errors.monthly}
              />
            </MarginBottom>
            <Label>Facebook URL</Label>
            <MarginBottom>
              <Input.Input
                type="text"
                name="feacbookUrl"
                placeholder="Your answer"
                value={signUpForm.values.feacbookUrl}
                handleOnChange={(e) => signUpForm.change('feacbookUrl', e.target.value)}
              />
            </MarginBottom>
            <Label>Instagram URL</Label>
            <MarginBottom>
              <Input.Input
                type="text"
                name="instagramUrl"
                placeholder="Your answer"
                value={signUpForm.values.instagramUrl}
                handleOnChange={(e) => signUpForm.change('instagramUrl', e.target.value)}
              />
            </MarginBottom>
            <Label>Twitter URL</Label>
            <MarginBottom>
              <Input.Input
                type="text"
                name="twitterUrl"
                placeholder="Your answer"
                value={signUpForm.values.twitterUrl}
                handleOnChange={(e) => signUpForm.change('twitterUrl', e.target.value)}
              />
            </MarginBottom>
            <Label>YouTube URL</Label>
            <MarginBottom>
              <Input.Input
                type="text"
                name="youtubeUrl"
                placeholder="Your answer"
                value={signUpForm.values.youtubeUrl}
                handleOnChange={(e) => signUpForm.change('youtubeUrl', e.target.value)}
              />
            </MarginBottom>
            <CheckBox
              checked={signUpForm.values.isGdpr}
              onChange={() => signUpForm.change('isGdpr', !signUpForm.values.isGdpr)}
              renderLabel={() => (
                <LabelCheckbox>
                  <Label margin="2px 0px 0px">GDPR Consent<Important>*</Important></Label>
                  By clicking yes, I confirm that I have read and agree to allow TakeMeTour Pte. Ltd to collect, use and/or process personal data about me that I have previously provided to TakeMeTour in accordance to the GDPR Act.
                </LabelCheckbox>
              )}
            />
            <CheckBox
              checked={signUpForm.values.isAgree}
              onChange={() => signUpForm.change('isAgree', !signUpForm.values.isAgree)}
              renderLabel={() => (
                <LabelCheckbox>
                  I agree to the <TextBlue><Link href="https://www.takemetour.com/article/terms-conditions-and-disclaimer" target="_blank" rel="noopener noreferrer">terms and conditions</Link></TextBlue>
                </LabelCheckbox>
              )}
            />
            <Submit
              // onClick={() => onSubmit(signUpForm.values)}
              disabled={isDisabled}
            >
              Submit
            </Submit>
            <TextError>{props.signupErrorMessage}</TextError>
          </Padding>
        </FormContainer>
      )}
    </Container>
  );
};

export default enhance(Register);
